import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Plus, Pencil, Trash2, Check, Tag } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { buildTeamApiService } from '../../services/buildTeamApi';
import type { BuildTag } from '../../types/buildTeam';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

interface BuildTagManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DESCRIPTION_TRUNCATE_LENGTH = 40;

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

function normalizeHexColor(value: string): string {
  const cleaned = value.replace(/^#/, '').trim();
  if (/^[0-9A-Fa-f]{6}$/.test(cleaned)) {
    return `#${cleaned}`;
  }
  if (/^[0-9A-Fa-f]{3}$/.test(cleaned)) {
    return `#${cleaned[0]}${cleaned[0]}${cleaned[1]}${cleaned[1]}${cleaned[2]}${cleaned[2]}`;
  }
  return value.startsWith('#') ? value : `#${value}`;
}

export function BuildTagManagementModal({ isOpen, onClose }: BuildTagManagementModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [tags, setTags] = useState<BuildTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createName, setCreateName] = useState('');
  const [createColor, setCreateColor] = useState('#3b82f6');
  const [createDescription, setCreateDescription] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const fetchTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await buildTeamApiService.listTags();
      setTags(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!createName.trim()) {
      setError('Name is required');
      return;
    }
    const color = normalizeHexColor(createColor);
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setError('Please enter a valid hex color (e.g. #3b82f6)');
      return;
    }
    setCreateSubmitting(true);
    try {
      const created = await buildTeamApiService.createTag({
        name: createName.trim(),
        color,
        description: createDescription.trim() || undefined,
      });
      setTags((prev) => [...prev, created]);
      setCreateName('');
      setCreateColor('#3b82f6');
      setCreateDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const startEdit = (tag: BuildTag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color.startsWith('#') ? tag.color : `#${tag.color}`);
    setEditDescription(tag.description ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('');
    setEditDescription('');
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    setError(null);
    if (!editName.trim()) {
      setError('Name is required');
      return;
    }
    const color = normalizeHexColor(editColor);
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setError('Please enter a valid hex color (e.g. #3b82f6)');
      return;
    }
    setEditSubmitting(true);
    try {
      const updated = await buildTeamApiService.updateTag(editingId, {
        name: editName.trim(),
        color,
        description: editDescription.trim() || null,
      });
      setTags((prev) => prev.map((t) => (t.id === editingId ? updated : t)));
      cancelEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tag');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    setDeleteSubmitting(true);
    try {
      await buildTeamApiService.deleteTag(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
      setDeleteConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tag');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-tags-title"
      >
        <div className="modal-header">
          <h2 id="manage-tags-title" className="modal-title flex items-center gap-2">
            <Tag className="w-5 h-5 text-accent-300" />
            Manage Tags
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close touch-manipulation"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleCreate} className="space-y-3 mb-6 pb-5 border-b border-glass-200/10">
          <div>
            <label className={LABEL_CLASS}>Name *</label>
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="glass-input w-full"
              placeholder="Tag name"
              required
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Color</label>
            <div className="flex items-center gap-2 flex-wrap">
              <div
                className="w-10 h-10 rounded-lg border border-glass-200/20 flex-shrink-0"
                style={{ backgroundColor: createColor.startsWith('#') ? createColor : `#${createColor}` }}
              />
              <input
                type="text"
                value={createColor}
                onChange={(e) => setCreateColor(e.target.value)}
                className="glass-input flex-1 min-w-[100px] font-mono"
                placeholder="#3b82f6"
                aria-label="Hex color"
              />
              <input
                type="color"
                value={createColor.startsWith('#') ? createColor : `#${createColor}`}
                onChange={(e) => setCreateColor(e.target.value)}
                className="w-10 h-10 rounded-lg cursor-pointer border border-glass-200/20 bg-transparent flex-shrink-0 p-0"
                title="Pick color"
                aria-label="Pick color"
              />
            </div>
          </div>
          <div>
            <label className={LABEL_CLASS}>Description</label>
            <input
              type="text"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              className="glass-input w-full"
              placeholder="Optional description"
            />
          </div>
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={createSubmitting}
          >
            {createSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Tag
          </button>
        </form>

        <div className="space-y-2">
          <h3 className="eyebrow mb-2">Existing Tags</h3>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="subcard p-3 flex items-center gap-3">
                  <SkeletonLoader className="w-5 h-5 rounded-md" />
                  <div className="flex-1 space-y-1.5">
                    <SkeletonLoader className="h-4 w-32" />
                    <SkeletonLoader className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : tags.length === 0 ? (
            <EmptyState
              bare
              compact
              icon={Tag}
              title="No tags yet"
              description="Create one above."
            />
          ) : (
            <ul className="space-y-2">
              {tags.map((tag) => (
                <li key={tag.id}>
                  {editingId === tag.id ? (
                    <div className="subcard p-3 space-y-3 border-accent-500/30">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="glass-input w-full"
                        placeholder="Name"
                        aria-label="Tag name"
                      />
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-md border border-glass-200/20 flex-shrink-0"
                          style={{
                            backgroundColor: editColor.startsWith('#') ? editColor : `#${editColor}`,
                          }}
                        />
                        <input
                          type="text"
                          value={editColor}
                          onChange={(e) => setEditColor(e.target.value)}
                          className="glass-input flex-1 font-mono"
                          placeholder="#3b82f6"
                          aria-label="Hex color"
                        />
                        <input
                          type="color"
                          value={editColor.startsWith('#') ? editColor : `#${editColor}`}
                          onChange={(e) => setEditColor(e.target.value)}
                          className="w-8 h-8 rounded-md cursor-pointer border border-glass-200/20 bg-transparent flex-shrink-0 p-0"
                          aria-label="Pick color"
                        />
                      </div>
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="glass-input w-full"
                        placeholder="Description"
                        aria-label="Tag description"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="glass-button-secondary flex-1"
                          disabled={editSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleUpdate}
                          className="btn-primary flex-1"
                          disabled={editSubmitting}
                        >
                          {editSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : deleteConfirmId === tag.id ? (
                    <div className="subcard p-3 flex items-center justify-between gap-3 flex-wrap border-red-500/25">
                      <span className="text-sm text-glass-300">Delete &quot;{tag.name}&quot;?</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(null)}
                          className="glass-button-secondary"
                          disabled={deleteSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(tag.id)}
                          className="btn-danger"
                          disabled={deleteSubmitting}
                        >
                          {deleteSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          Delete
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="subcard p-3 flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-md flex-shrink-0 border border-glass-200/20"
                        style={{
                          backgroundColor: tag.color.startsWith('#') ? tag.color : `#${tag.color}`,
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-white block truncate">{tag.name}</span>
                        {tag.description && (
                          <span className="text-xs text-glass-400 block truncate" title={tag.description}>
                            {tag.description.length > DESCRIPTION_TRUNCATE_LENGTH
                              ? `${tag.description.slice(0, DESCRIPTION_TRUNCATE_LENGTH)}…`
                              : tag.description}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => startEdit(tag)}
                          className="btn-icon touch-manipulation"
                          aria-label="Edit"
                          title="Edit tag"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(tag.id)}
                          className="btn-icon text-red-400 hover:text-red-300 touch-manipulation"
                          aria-label="Delete"
                          title="Delete tag"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
